// ModalHistoricoProduto — historico de UM produto interno em todos os fornecedores.
// Portado de ControleFC/frontend/src/components/ModalHistoricoProduto.jsx.
import { useEffect, useMemo, useState } from 'react';
import { Loader2, ImageOff } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { FotoProduto } from '../../components/FotoProduto';
import { formatMoeda, formatDataSP } from '@fiodecobre/shared';

export interface ProdutoHistorico {
  id: number;
  codigo_interno?: string | null;
  descricao?: string | null;
  foto_url?: string | null;
}

interface LinhaServidor {
  id: string;
  fornecedor_id: number;
  fornecedor_nome: string | null;
  codigo_fornecedor: string | null;
  descricao_fornecedor: string | null;
  marca: string | null;
  embalagem: string | null;
  quantidade_embalagem: string | number | null;
  quantidade_cotada: string | number | null;
  preco_unitario: string | number | null;
  ncm: string | null;
  ultima_cotacao_id: number | null;
  visto_em: string | null;
  origem: 'catalogo' | 'cotacao';
  cotacao_numero: string | null;
}

type SortKey =
  | 'fornecedor_nome'
  | 'codigo_fornecedor'
  | 'descricao_fornecedor'
  | 'marca'
  | 'embalagem'
  | 'quantidade_embalagem'
  | 'preco_emb'
  | 'preco_unit_real'
  | 'origem'
  | 'visto_em';

interface Props {
  aberto: boolean;
  onFechar: () => void;
  produto: ProdutoHistorico | null;
  excluirCotacaoId?: number | null;
}

export function ModalHistoricoProduto({ aberto, onFechar, produto, excluirCotacaoId }: Props) {
  const [linhas, setLinhas] = useState<LinhaServidor[]>([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('preco_unit_real');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  useEffect(() => {
    if (!aberto || !produto?.id) return;
    let cancelado = false;
    setLoading(true);
    setErro('');
    const qs = excluirCotacaoId ? `?excluir_cotacao=${excluirCotacaoId}` : '';
    api
      .get<LinhaServidor[]>(`/api/fornecedor-catalogo/historico/${produto.id}${qs}`)
      .then((data) => {
        if (!cancelado) setLinhas(data || []);
      })
      .catch((err: unknown) => {
        if (cancelado) return;
        setLinhas([]);
        setErro(err instanceof ApiError ? err.message : 'erro ao carregar historico');
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [aberto, produto?.id, excluirCotacaoId]);

  // preco_unitario do banco = preco da embalagem. Preco unitario REAL = preco_emb / qtd_embalagem.
  const enriquecidas = useMemo(() => {
    return linhas.map((l) => {
      const pe = Number(l.preco_unitario);
      const qe = Number(l.quantidade_embalagem);
      const precoUnitReal =
        Number.isFinite(pe) && pe > 0 && Number.isFinite(qe) && qe > 0 ? pe / qe : null;
      return { ...l, preco_emb: pe, preco_unit_real: precoUnitReal };
    });
  }, [linhas]);

  const menorUnit = useMemo(() => {
    const precos = enriquecidas
      .map((l) => l.preco_unit_real)
      .filter((p): p is number => Number.isFinite(p) && (p as number) > 0);
    return precos.length > 0 ? Math.min(...precos) : null;
  }, [enriquecidas]);

  const ordenadas = useMemo(() => {
    const arr = [...enriquecidas];
    arr.sort((a: any, b: any) => {
      let va = a[sortKey];
      let vb = b[sortKey];
      if (sortKey === 'preco_emb' || sortKey === 'preco_unit_real' || sortKey === 'quantidade_embalagem') {
        const na = Number(va);
        const nb = Number(vb);
        const aFinite = Number.isFinite(na);
        const bFinite = Number.isFinite(nb);
        if (!aFinite && !bFinite) return 0;
        if (!aFinite) return 1;
        if (!bFinite) return -1;
        return sortDir === 'asc' ? na - nb : nb - na;
      }
      if (sortKey === 'visto_em') {
        const da = va ? new Date(va).getTime() : 0;
        const db = vb ? new Date(vb).getTime() : 0;
        return sortDir === 'asc' ? da - db : db - da;
      }
      const sa = String(va || '').toUpperCase();
      const sb = String(vb || '').toUpperCase();
      if (sa < sb) return sortDir === 'asc' ? -1 : 1;
      if (sa > sb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [enriquecidas, sortKey, sortDir]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'preco_unit_real' || key === 'preco_emb' ? 'asc' : 'desc');
    }
  }


  if (!aberto) return null;

  function colHeader(label: string, key: SortKey, align: 'left' | 'center' | 'right' = 'left') {
    const isSort = sortKey === key;
    const arrow = isSort ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
    return (
      <th
        className={`px-3 py-2 font-semibold text-xs cursor-pointer select-none hover:bg-blue-50 text-${align}`}
        style={{ backgroundColor: 'var(--grid-header-bg)', color: 'var(--grid-header-fg)' }}
        onClick={() => toggleSort(key)}
      >
        {label}
        {arrow}
      </th>
    );
  }

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div
        className="rounded-2xl shadow-2xl w-full max-w-6xl flex flex-col"
        style={{ backgroundColor: 'var(--panel-bg)', maxHeight: 'calc(100vh - 32px)' }}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div className="flex items-center gap-3 min-w-0">
            {produto?.foto_url ? (
              <FotoProduto url={produto.foto_url} tam={48} style={{ flexShrink: 0 }} />
            ) : (
              <div className="w-12 h-12 bg-gray-100 rounded border border-gray-200 flex items-center justify-center flex-shrink-0">
                <ImageOff size={16} className="text-gray-300" />
              </div>
            )}
            <div className="min-w-0">
              <div className="text-xs text-gray-500 uppercase">
                Historico do produto em todos os fornecedores
              </div>
              <div className="flex items-baseline gap-2">
                {produto?.codigo_interno && (
                  <span className="font-mono font-bold text-gray-500 no-underline">
                    {produto.codigo_interno}
                  </span>
                )}
                <span className="text-base font-semibold text-gray-800 truncate">
                  {produto?.descricao || '—'}
                </span>
              </div>
            </div>
          </div>
          <button type="button" className="btn-fechar-modal" onClick={onFechar} aria-label="Fechar">×</button>
        </div>

        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="flex items-center justify-center py-16 text-gray-400">
              <Loader2 size={20} className="animate-spin mr-2" />
              Carregando historico...
            </div>
          )}
          {!loading && erro && <div className="p-6 text-center text-sm text-red-600">{erro}</div>}
          {!loading && !erro && ordenadas.length === 0 && (
            <div className="p-12 text-center text-sm text-gray-400">
              Sem historico desse produto em outros fornecedores.
              <br />
              <span className="text-[11px]">
                Aparece aqui depois que voce salvar respostas em cotacoes ou cadastrar pelo Catalogo.
              </span>
            </div>
          )}
          {!loading && !erro && ordenadas.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr>
                  {colHeader('Fornecedor', 'fornecedor_nome')}
                  {colHeader('Cod. Fornec.', 'codigo_fornecedor')}
                  {colHeader('Descricao', 'descricao_fornecedor')}
                  {colHeader('Marca', 'marca')}
                  {colHeader('Embal.', 'embalagem', 'center')}
                  {colHeader('Qtd na Emb', 'quantidade_embalagem', 'right')}
                  {colHeader('Preco Emb', 'preco_emb', 'right')}
                  {colHeader('Preco Unit.', 'preco_unit_real', 'right')}
                  {colHeader('Origem', 'origem', 'center')}
                  {colHeader('Visto em', 'visto_em', 'center')}
                </tr>
              </thead>
              <tbody>
                {ordenadas.map((l, idx) => {
                  const pu = l.preco_unit_real;
                  const isMenor =
                    Number.isFinite(pu) &&
                    (pu as number) > 0 &&
                    menorUnit != null &&
                    Math.abs((pu as number) - menorUnit) < 0.0001;
                  return (
                    <tr
                      key={l.id}
                      className="border-b border-gray-100"
                      style={{
                        backgroundColor: isMenor
                          ? '#fde68a'
                          : idx % 2 === 1
                            ? 'var(--grid-row-alt-bg)'
                            : 'var(--grid-row-bg)',
                        color: isMenor ? '#000' : 'var(--grid-row-fg)',
                      }}
                    >
                      <td className="px-3 py-2 text-xs font-semibold text-gray-700">
                        {(l.fornecedor_nome || '').toUpperCase() || (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{l.codigo_fornecedor || '—'}</td>
                      <td
                        className="px-3 py-2 text-xs text-gray-600 truncate max-w-[280px]"
                        title={l.descricao_fornecedor || ''}
                      >
                        {l.descricao_fornecedor || '—'}
                      </td>
                      <td className="px-3 py-2 text-xs">{l.marca || '—'}</td>
                      <td className="px-3 py-2 text-xs text-center">{l.embalagem || '—'}</td>
                      <td className="px-3 py-2 text-xs text-right">
                        {l.quantidade_embalagem != null ? Number(l.quantidade_embalagem) : '—'}
                      </td>
                      <td className="px-3 py-2 text-xs text-right">
                        {Number.isFinite(l.preco_emb) && l.preco_emb > 0 ? (
                          formatMoeda(l.preco_emb)
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td
                        className="px-3 py-2 text-xs text-right font-semibold"
                        style={isMenor ? { color: '#000' } : undefined}
                      >
                        {Number.isFinite(l.preco_unit_real) && (l.preco_unit_real as number) > 0 ? (
                          formatMoeda(l.preco_unit_real as number)
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {l.origem === 'catalogo' ? (
                          <span className="inline-block px-1.5 py-0.5 text-[9px] font-bold rounded bg-purple-100 text-purple-700">
                            CAT
                          </span>
                        ) : (
                          <span
                            className="inline-block px-1.5 py-0.5 text-[9px] font-bold rounded bg-blue-100 text-blue-700"
                            title={l.cotacao_numero || ''}
                          >
                            {l.cotacao_numero || 'COT'}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-center text-gray-500">
                        {l.visto_em ? formatDataSP(l.visto_em) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-2.5 border-t border-gray-200 text-xs text-gray-500">
          <span>
            {ordenadas.length} {ordenadas.length === 1 ? 'registro' : 'registros'}
            {menorUnit != null && (
              <>
                {' '}
                — menor preco unit em{' '}
                <span
                  className="font-semibold"
                  style={{
                    backgroundColor: '#fde68a',
                    color: '#000',
                    padding: '0 6px',
                    borderRadius: 3,
                  }}
                >
                  amarelo
                </span>
              </>
            )}
          </span>
          <button
            type="button"
            onClick={onFechar}
            className="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded-lg transition"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
